// This is a mixin which provides an object with the ability to render Handlebars templates.
// It allows components to load remote templates, compile them with Handlebars, and render them with data.
// Pass in obj to extend the obj's prototype with the methods.

import Handlebars from "handlebars";
import { JsonFetcher } from "../json_fetcher";
import { identifyElement } from "../../helpers/element";

export function MixinTemplateRenderable(obj) {
  mixinMethods(obj);
}

function mixinMethods(obj) {
  // Initialize template cache and data properties if they don't exist
  obj.templateCache = obj.templateCache || {};
  obj.templateData = obj.templateData || {};

  Object.assign(obj.__proto__, {
    /**
     * Initialize template rendering capabilities
     * @param {Object} options - Configuration options
     * @param {string} options.templateEndpoint - endpoint to fetch the Handlebars template from (relatively or full endpoint)
     * @param {string} options.dataEndpoint - endpoint to fetch the template data from (relative or full endpoint)
     * @param {string} options.templateString - Direct template string (alternative to templateEndpoint)
     * @param {Object} options.data - Direct data object (alternative to dataEndpoint)
     * @param {string} options.templateCacheKey - Key to use for caching the template
     * @param {boolean} options.autoRender - Whether to automatically render when data changes
     * @param {string} options.targetSelector - Selector for the element to render the template into (defaults to this.element)
     */
    initializeTemplate(options = {}) {
      Object.assign(
        this,
        {
          templateEndpoint: null,
          dataEndpoint: null,
          templateString: null,
          templateData: {},
          templateCacheKey: "default",
          autoRender: true,
          targetSelector: null,
        },
        options,
      );

      this.templateIsRendered = false; // Reset the rendered state

      // Allow consumers to skip auto rendering if desired
      if (this.autoRender === false) return;

      this.loadAndRender();
    },

    /**
     * Fetch template and data, then render once both are available.
     * @returns {Promise} - Resolves when rendering is complete
     */
    loadAndRender() {
      const templatePromise = this.templateString
        ? Promise.resolve(this.templateString)
        : this.fetchTemplate();

      const dataPromise =
        this.templateData && Object.keys(this.templateData).length > 0
          ? Promise.resolve(this.templateData)
          : this.fetchTemplateData();

      return Promise.all([templatePromise, dataPromise])
        .then(([templateString, data]) => {
          // Compile only if template wasn't already compiled via fetchTemplate()
          if (!this.templateCache[this.templateCacheKey] && templateString) {
            this.compileTemplate(templateString);
          }

          this.setTemplateData(data); // This also calls renderTemplate() if autoRender
        })
        .catch((error) => {
          console.error("[TemplateRenderable] loadAndRender() failed:", error);
          const targetElement = this.getTargetElement();
          if (targetElement) {
            targetElement.innerHTML =
              '<div class="error">Failed to load component</div>';
          }
        });
    },

    /**
     * Fetch a template from a remote endpoint
     * @returns {Promise} - Promise that resolves when the template is fetched and compiled
     */
    fetchTemplate() {
      if (!this.templateEndpoint) {
        console.error("[TemplateRenderable] No template endpoint provided");
        return Promise.reject(new Error("No template endpoint provided"));
      }

      return new Promise((resolve, reject) => {
        fetch(this.templateEndpoint)
          .then((response) => {
            if (!response.ok) {
              throw new Error(
                `Failed to fetch template: ${response.status} ${response.statusText}`,
              );
            }
            return response.text();
          })
          .then((templateString) => {
            this.templateString = templateString;
            this.compileTemplate(templateString);
            resolve(templateString);
          })
          .catch((error) => {
            console.error(
              "[TemplateRenderable] Error fetching template:",
              error,
            );
            reject(error);
          });
      });
    },

    /**
     * Fetch data for the template from a remote endpoint
     * @returns {Promise} - Promise that resolves when the data is fetched
     */
    fetchTemplateData() {
      if (!this.dataEndpoint) {
        console.error("[TemplateRenderable] No data endpoint provided");
        return Promise.reject(new Error("No data endpoint provided"));
      }

      return new Promise((resolve, reject) => {
        const jsonFetcher = new JsonFetcher(this.dataEndpoint);
        jsonFetcher
          .fetchData()
          .then((data) => {
            this.setTemplateData(data);
            resolve(data);
          })
          .catch((error) => {
            console.error(
              "[TemplateRenderable] Error fetching template data:",
              error,
            );
            reject(error);
          });
      });
    },

    /**
     * Compile a template string with Handlebars
     * @param {string} templateString - The template string to compile
     * @returns {Function} - The compiled template function
     */
    compileTemplate(templateString) {
      if (!templateString) {
        console.error("[TemplateRenderable] No template string provided");
        return null;
      }

      try {
        const compiledTemplate = Handlebars.compile(templateString);
        this.templateCache[this.templateCacheKey] = compiledTemplate;
        return compiledTemplate;
      } catch (error) {
        console.error("[TemplateRenderable] Error compiling template:", error);
        return null;
      }
    },

    /**
     * Set the data for the template
     * @param {Object} data - The data to use for rendering the template
     */
    setTemplateData(data) {
      this.templateData = data;

      // If autoRender is enabled, render the template with the new data
      if (this.autoRender) {
        this.renderTemplate();
      }
    },

    /**
     * Update a specific property in the template data
     * @param {string} key - The key to update
     * @param {*} value - The new value
     */
    updateTemplateData(key, value) {
      if (!this.templateData) {
        this.templateData = {};
      }

      this.templateData[key] = value;

      this.templateIsRendered = false; // Reset the rendered state

      // If autoRender is enabled, render the template with the updated data
      if (this.autoRender) {
        this.renderTemplate();
      }
    },

    /**
     * Render the template with the current data
     * @returns {string} - The rendered HTML
     */
    renderTemplate() {
      const compiledTemplate = this.templateCache[this.templateCacheKey];

      if (!compiledTemplate) {
        console.warn("[TemplateRenderable] No compiled template available");
        return null;
      }

      try {
        const renderedHtml = compiledTemplate(this.templateData);

        // If we have a target element, update its content
        const targetElement = this.getTargetElement();
        if (targetElement) {
          targetElement.innerHTML = renderedHtml;
          this.templateIsRendered = true;
          if (this.render && typeof this.render === "function") this.render(); // Call the render method if it exists
        } else {
          console.warn(
            "[TemplateRenderable] No target element found for rendering",
          );
        }

        return renderedHtml;
      } catch (error) {
        console.error("[TemplateRenderable] Error rendering template:", error);
        return null;
      }
    },

    /**
     * Get the target element for rendering the template.
     * If a target selector is not specified then create a target element.
     * @returns {Element} - The target DOM element
     */
    getTargetElement() {
      if (this.targetSelector) {
        const element = this.element.querySelector(this.targetSelector);
        if (!element) {
          console.error(
            "[TemplateRenderable] No target element found for selector:",
            this.targetSelector,
          );
        }
        return element;
      } else {
        const element = document.createElement("div");
        const id = identifyElement(element);
        this.element.appendChild(element);
        this.targetSelector = `#${id}`;
        return element;
      }
    },

    /**
     * Register a Handlebars helper for use in templates
     * @param {string} name - The name of the helper
     * @param {Function} helperFn - The helper function
     */
    registerTemplateHelper(name, helperFn) {
      Handlebars.registerHelper(name, helperFn);
    },

    /**
     * Register a Handlebars partial for use in templates
     * @param {string} name - The name of the partial
     * @param {string} partialTemplate - The partial template string
     */
    registerTemplatePartial(name, partialTemplate) {
      Handlebars.registerPartial(name, partialTemplate);
    },
  });
}
